import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Button,
  Box,
  Paper,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  OutlinedInput,
  InputAdornment,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { DataGrid, getGridSingleSelectOperators } from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import Popover from "@mui/material/Popover";
import { format } from "date-fns";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import SnackBar from "components/SnackBar/SnackBar";
import BackdropComponent from "components/Spinner/Spinner";
import exportAsCsv from "utils/exportAsCsv";
import {
  getMergeClassTraineeMilestoneDetails,
  getMilestoneByMergeRosterId,
  updateMergeClassRosterCurriculumStatus,
  getCurriculumStatus,
  getTraineeStatusList,
  getMergeClassRosterDetails,
  getSearchPopoverContent,
  updateMergeClassTraineeMilestoneGridDetails,
  getSearchValue,
} from "../../../services/learnPointUserServices";
import classes from "../ClassRoster/LearnPointClassRoster.module.css";

import {
  LimitChip,
  StyledChip,
} from "components/IdmCredentialTracking/NoRosterIdCreateUpdateIdmRecord";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useAuth } from "context/AuthContext";

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const CustomNoRowsOverlay = () => (
  <div className={classes.noRecordsOverlay}>
    <h3>No results to display.</h3>
  </div>
);

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .MuiTooltip-tooltip`]: {
    whiteSpace: "normal",
    fontSize: "0.85rem",
    maxWidth: "150px",
  },
});

const PaperComponentTemplate = (props) => <Paper {...props} />;

const ItemHeading = styled(Paper)((props) => ({
  textAlign: "center",
  boxShadow: "none",
  fontWeight: 600,
  ...(props?.disabled && { paddingTop: 12 }),
}));

ItemHeading.propTypes = {
  disabled: PropTypes.bool,
};

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

// ============================================================================
// CELL COMPONENTS FOR DYNAMIC COLUMNS
// ============================================================================

const BooleanMilestoneCell = ({ params, isMilestoneApplicable }) => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: !isMilestoneApplicable ? "#d3d3d3" : "transparent",
    }}
  >
    <input
      type="checkbox"
      checked={params.value || false}
      disabled={true}
      readOnly
      style={{
        cursor: !isMilestoneApplicable ? "not-allowed" : "default",
      }}
    />
  </Box>
);

// ============================================================================
// MULTISELECT FIX: Enhanced display cell to handle multiple data formats
// Converts both string (from database) and array (during editing) to display format
// Handles null/undefined values and provides visual feedback for non-applicable milestones
// ============================================================================

const MultiSelectDisplayCell = ({ params, isMilestoneApplicable }) => {
  const value = params.value;
  let values = [];

  if (value && value !== "null" && value !== "undefined") {
    values = Array.isArray(value)
      ? value
      : value
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);
  }

  const displayValue = values.join(", ");

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: !isMilestoneApplicable ? "#d3d3d3" : "transparent",
        padding: "8px",
        opacity: !isMilestoneApplicable ? 0.6 : 1,
      }}
    >
      {displayValue}
    </Box>
  );
};

const MultiSelectNotApplicable = () => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      backgroundColor: "#d3d3d3",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "not-allowed",
    }}
  >
    <TextField
      disabled
      fullWidth
      value=""
      placeholder="Not applicable"
      size="small"
    />
  </Box>
);

// ============================================================================
// MULTISELECT FIX: Updated function signature to match MUI Autocomplete API
// MUI passes (value, getTagProps) directly, not as an object
// Added null/undefined safety check to prevent crashes when rendering chips
// ============================================================================

const MultiSelectTagRenderer = (value, getTagProps) => {
  if (!value || !Array.isArray(value) || value.length === 0) {
    return null;
  }

  const limitTags = 3;
  const more = value.length - limitTags;

  return (
    <>
      {value.slice(0, limitTags).map((option, index) => (
        <StyledChip
          key={index}
          label={`${option}`}
          size="small"
          {...getTagProps({ index })}
        />
      ))}
      {more > 0 && <LimitChip label={`+${more}`} size="small" />}
    </>
  );
};

// ============================================================================
// MULTISELECT FIX: Added local state management to maintain selected values during editing
// Uses useMemo to prevent unnecessary recalculations and useState for controlled component
// Keeps value as array during editing so Autocomplete displays chips correctly
// ============================================================================

const MultiSelectEditable = ({ params, getMultiSelectSelectedValues }) => {
  const initialValue = React.useMemo(
    () => getMultiSelectSelectedValues(params.value) || [],
    [],
  );

  const [selectedValues, setSelectedValues] = React.useState(initialValue);

  const handleChange = (event, newValue) => {
    setSelectedValues(newValue || []);
    params.api.setEditCellValue(
      { id: params.id, field: params.field, value: newValue || [] },
      event,
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Autocomplete
        multiple
        fullWidth
        disableCloseOnSelect
        options={params.colDef.valueOptions || []}
        value={selectedValues}
        onChange={handleChange}
        renderInput={(inputParams) => (
          <TextField {...inputParams} placeholder="Select Status" autoFocus />
        )}
        renderTags={MultiSelectTagRenderer}
      />
    </Box>
  );
};

const MultiSelectEditCell = ({
  params,
  isMilestoneApplicable,
  getMultiSelectSelectedValues,
}) => {
  if (!isMilestoneApplicable) {
    return <MultiSelectNotApplicable />;
  }

  return (
    <MultiSelectEditable
      params={params}
      getMultiSelectSelectedValues={getMultiSelectSelectedValues}
    />
  );
};

const MemoDisplayCell = ({ params, isMilestoneApplicable }) => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: !isMilestoneApplicable ? "#d3d3d3" : "transparent",
      padding: "8px",
      opacity: !isMilestoneApplicable ? 0.6 : 1,
    }}
  >
    {params.value || ""}
  </Box>
);

const MemoEditCell = ({ params, isMilestoneApplicable }) => {
  const handleChange = (event) => {
    if (isMilestoneApplicable) {
      params.api.setEditCellValue(
        {
          id: params.id,
          field: params.field,
          value: event.target.value,
        },
        event,
      );
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: !isMilestoneApplicable ? "#d3d3d3" : "transparent",
      }}
    >
      <TextField
        multiline
        minRows={1}
        maxRows={2}
        fullWidth
        disabled={!isMilestoneApplicable}
        value={params.value || ""}
        onChange={handleChange}
        sx={{
          "& .MuiInputBase-inputMultiline": {
            overflowY: "auto",
            maxHeight: "120px",
            cursor: !isMilestoneApplicable ? "not-allowed" : "text",
          },
        }}
        inputProps={{
          maxLength: 2000,
        }}
        helperText={
          isMilestoneApplicable
            ? params.value?.length >= 2000
              ? "Maximum 2000 characters reached"
              : `${params.value?.length || 0}/2000`
            : ""
        }
      />
    </Box>
  );
};

const DateDisplayCell = ({ params, isMilestoneApplicable }) => {
  let displayValue = "";
  if (params.value && params.value !== "null") {
    try {
      displayValue = format(new Date(params.value), "MM/dd/yyyy");
    } catch {
      displayValue = "";
    }
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: !isMilestoneApplicable ? "#d3d3d3" : "transparent",
        padding: "8px",
        opacity: !isMilestoneApplicable ? 0.6 : 1,
      }}
    >
      {displayValue}
    </Box>
  );
};

const DateEditCell = ({ params, isMilestoneApplicable, setError }) => {
  const handleDateChange = (newValue) => {
    if (!isMilestoneApplicable) {
      return;
    }

    if (newValue === null) {
      setError(false);
      params.api.setEditCellValue(
        { id: params.id, field: params.field, value: "" },
        { debounceMs: 200 },
      );
      return;
    }

    if (newValue?.isValid()) {
      const formatted = newValue.format("YYYY-MM-DD");
      setError(false);
      params.api.setEditCellValue(
        {
          id: params.id,
          field: params.field,
          value: formatted,
        },
        { debounceMs: 200 },
      );
    } else {
      setError(true);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: !isMilestoneApplicable ? "#d3d3d3" : "transparent",
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          clearable
          disabled={!isMilestoneApplicable}
          value={params.value ? dayjs(params.value) : null}
          onChange={handleDateChange}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              sx={{
                width: 150,
              }}
            />
          )}
        />
      </LocalizationProvider>
    </Box>
  );
};

const DefaultMilestoneCell = ({ params, isMilestoneApplicable }) => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: !isMilestoneApplicable ? "#d3d3d3" : "transparent",
      padding: "8px",
      opacity: !isMilestoneApplicable ? 0.6 : 1,
      cursor: !isMilestoneApplicable ? "not-allowed" : "default",
    }}
  >
    {params.value || ""}
  </Box>
);

// ============================================================================
// OPERATION LEAD CELL COMPONENTS
// ============================================================================

const SearchIconButton = ({
  classTraineeId,
  operationLeadName,
  searchHandler,
}) => (
  <InputAdornment position="end">
    <IconButton
      id={`icon-button-${classTraineeId}`}
      type="button"
      sx={{ p: "10px" }}
      aria-label="search"
      onClick={() => searchHandler(operationLeadName)}
    >
      <SearchIcon />
    </IconButton>
  </InputAdornment>
);

const OpsLeadInputField = ({
  params,
  setRows,
  validateAndGetOpsLeadName,
  searchHandler,
}) => {
  const handleChange = (event) => {
    setRows((prev) =>
      prev.map((item) =>
        validateAndGetOpsLeadName(item, params, event.target.value),
      ),
    );
  };

  return (
    <FormControl>
      <OutlinedInput
        id={`input-${params.row.ClassTraineeId}`}
        value={params?.row?.OperationLeadName}
        onChange={handleChange}
        endAdornment={
          <SearchIconButton
            classTraineeId={params.row.ClassTraineeId}
            operationLeadName={params.row.OperationLeadName}
            searchHandler={searchHandler}
          />
        }
        label="Ops Lead"
      />
    </FormControl>
  );
};

const OpsLeadAutocomplete = ({
  params,
  searchOpsLead,
  handleOpsLeadChange,
  PaperComponentTemplate,
  classes,
}) => (
  <Autocomplete
    id={`autocomplete-${params.row.ClassTraineeId}`}
    disablePortal
    freeSolo={true}
    openOnFocus
    disableClearable={true}
    className={classes.searchField}
    options={searchOpsLead}
    getOptionLabel={(option) => `${option.Name} (${option.Email})`}
    renderInput={(params) => <TextField {...params} label="Select Ops Lead" />}
    onChange={(event, values) => handleOpsLeadChange(values)}
    PaperComponent={PaperComponentTemplate}
  />
);

const OperationLeadEditCell = ({
  params,
  opsTrainerShow,
  setRows,
  validateAndGetOpsLeadName,
  searchHandler,
  searchOpsLead,
  handleOpsLeadChange,
  PaperComponentTemplate,
  classes,
}) => (
  <>
    {opsTrainerShow ? (
      <OpsLeadInputField
        params={params}
        setRows={setRows}
        validateAndGetOpsLeadName={validateAndGetOpsLeadName}
        searchHandler={searchHandler}
      />
    ) : (
      <OpsLeadAutocomplete
        params={params}
        searchOpsLead={searchOpsLead}
        handleOpsLeadChange={handleOpsLeadChange}
        PaperComponentTemplate={PaperComponentTemplate}
        classes={classes}
      />
    )}
  </>
);

// ============================================================================
// HELPER FUNCTIONS - Validation
// ============================================================================

const hasValidationError = (error, setSnack) => {
  if (error) {
    setSnack({
      open: true,
      message: "Invalid dates couldn't be saved",
      severity: "error",
    });
    return true;
  }
  return false;
};

const isCellEditable = (column, changedRow, id, field, setSnack) => {
  if (!column || typeof column.editable !== "function") {
    return true;
  }

  const isEditable = column.editable({ row: changedRow, id, field });

  if (!isEditable) {
    setSnack({
      open: true,
      message: "This milestone is not applicable to this trainee's roster.",
      severity: "warning",
    });
    return false;
  }

  return true;
};

const shouldSkipField = (field) => {
  return field === "OperationLeadName" || field === "TraineeStatus";
};

const hasValueChanged = (changedRow, field, value) => {
  return changedRow[field] !== value;
};

const isDateField = (field, columns) => {
  return (
    field === "ProductionDate" ||
    columns.find((col) => col.field === field)?.type === "date"
  );
};

const formatDateValue = (value) => {
  if (value === "" || value === null) {
    return null;
  }
  return format(new Date(value), "MM/dd/yyyy");
};

const getUpdatedValue = (field, value, initialValue, columns) => {
  if (isDateField(field, columns)) {
    return formatDateValue(value);
  }

  if (value === null && initialValue) {
    return null;
  }

  return value;
};

// ============================================================================
// HELPER FUNCTIONS - Update Handlers
// ============================================================================

const processRowsFromResponse = (updateClassTraineeGrid) => {
  const rowsData = Object.values(updateClassTraineeGrid.data)?.map((item) => ({
    ...item,
    id: item.ClassTraineeId,
  }));

  return rowsData.filter(
    (column) => column["TraineeStatus"]?.toLowerCase() !== "added in error",
  );
};

const handleSuccessfulUpdate = (
  updateClassTraineeGrid,
  setRows,
  setLoading,
  setSnack,
  setActiveEditTrainee,
) => {
  const processedRows = processRowsFromResponse(updateClassTraineeGrid);
  setRows(processedRows);
  setActiveEditTrainee(null);
  setLoading(false);
  setSnack({
    open: true,
    message: "Record has been successfully updated.",
    severity: "success",
  });
};

const handleFailedUpdate = (setLoading, setSnack) => {
  setLoading(false);
  setSnack({
    open: true,
    message: "Encounter error while updating changes.",
    severity: "error",
  });
};

const handleNoChanges = (setLoading, setSnack) => {
  setLoading(false);
  setSnack({
    open: true,
    message: "No changes detected.",
    severity: "info",
  });
};

const performUpdate = async (
  classRosterId,
  id,
  field,
  updatedValue,
  prepareUpdateValue,
  setLoading,
  setRows,
  setSnack,
  setActiveEditTrainee,
) => {
  setLoading(true);

  const updateClassTraineeGrid =
    await updateMergeClassTraineeMilestoneGridDetails(
      classRosterId,
      id,
      field,
      prepareUpdateValue(updatedValue),
    );

  if (updateClassTraineeGrid?.message === "Success") {
    handleSuccessfulUpdate(
      updateClassTraineeGrid,
      setRows,
      setLoading,
      setSnack,
      setActiveEditTrainee,
    );
  } else {
    handleFailedUpdate(setLoading, setSnack);
  }
};

// ============================================================================
// HELPER FUNCTIONS - Column Building
// ============================================================================

const prepareDynamicColumnsValueOptions = (columnItem) => {
  if (columnItem !== "NA") {
    return columnItem.split(",");
  }
  return null;
};

const buildBooleanColumn = (isMilestoneApplicableToRoster, milestoneName) => ({
  renderCell: (params) => {
    const isMilestoneApplicable = isMilestoneApplicableToRoster(
      params.row.ClassRosterId,
      milestoneName,
    );
    return (
      <BooleanMilestoneCell
        params={params}
        isMilestoneApplicable={isMilestoneApplicable}
      />
    );
  },
});

const buildMultiSelectColumn = (
  isMilestoneApplicableToRoster,
  milestoneName,
  getMultiSelectSelectedValues,
) => ({
  renderCell: (params) => {
    const isMilestoneApplicable = isMilestoneApplicableToRoster(
      params.row.ClassRosterId,
      milestoneName,
    );
    return (
      <MultiSelectDisplayCell
        params={params}
        isMilestoneApplicable={isMilestoneApplicable}
      />
    );
  },
  renderEditCell: (params) => {
    const isMilestoneApplicable = isMilestoneApplicableToRoster(
      params.row.ClassRosterId,
      milestoneName,
    );
    return (
      <MultiSelectEditCell
        params={params}
        isMilestoneApplicable={isMilestoneApplicable}
        getMultiSelectSelectedValues={getMultiSelectSelectedValues}
      />
    );
  },
  width: 400,
});

const buildMemoColumn = (isMilestoneApplicableToRoster, milestoneName) => ({
  renderCell: (params) => {
    const isMilestoneApplicable = isMilestoneApplicableToRoster(
      params.row.ClassRosterId,
      milestoneName,
    );
    return (
      <MemoDisplayCell
        params={params}
        isMilestoneApplicable={isMilestoneApplicable}
      />
    );
  },
  renderEditCell: (params) => {
    const isMilestoneApplicable = isMilestoneApplicableToRoster(
      params.row.ClassRosterId,
      milestoneName,
    );
    return (
      <MemoEditCell
        params={params}
        isMilestoneApplicable={isMilestoneApplicable}
      />
    );
  },
  width: 400,
});

const buildDateColumn = (
  isMilestoneApplicableToRoster,
  milestoneName,
  setError,
) => ({
  renderCell: (params) => {
    const isMilestoneApplicable = isMilestoneApplicableToRoster(
      params.row.ClassRosterId,
      milestoneName,
    );
    return (
      <DateDisplayCell
        params={params}
        isMilestoneApplicable={isMilestoneApplicable}
      />
    );
  },
  renderEditCell: (params) => {
    const isMilestoneApplicable = isMilestoneApplicableToRoster(
      params.row.ClassRosterId,
      milestoneName,
    );
    return (
      <DateEditCell
        params={params}
        isMilestoneApplicable={isMilestoneApplicable}
        setError={setError}
      />
    );
  },
  width: 200,
  type: "date",
  valueFormatter: (params) => {
    if (!params.value || params.value === "null") return "";
    try {
      return format(new Date(params.value), "MM/dd/yyyy");
    } catch {
      return "";
    }
  },
  valueGetter: (params) => {
    if (!params.value || params.value === "null") return "";
    return params.value;
  },
});

const buildDefaultColumn = (isMilestoneApplicableToRoster, milestoneName) => ({
  renderCell: (params) => {
    const isMilestoneApplicable = isMilestoneApplicableToRoster(
      params.row.ClassRosterId,
      milestoneName,
    );
    return (
      <DefaultMilestoneCell
        params={params}
        isMilestoneApplicable={isMilestoneApplicable}
      />
    );
  },
});

const buildDynamicColumn = (
  milestoneName,
  milestoneDefinitions,
  isMilestoneApplicableToRoster,
  prepareDynamicColumnsValueOptions,
  getMultiSelectSelectedValues,
  setError,
  classes,
) => {
  const milestoneValue = milestoneDefinitions[milestoneName];
  const [rawType, rawOptions] = milestoneValue?.split("-");
  const type = rawType?.trim().toLowerCase();
  const options = rawOptions?.trim();

  const baseColumn = {
    field: milestoneName,
    headerName: milestoneName,
    headerClassName: classes.headerClass,
    headerAlign: "center",
    ...(options && {
      valueOptions: prepareDynamicColumnsValueOptions(options),
    }),
    ...(type !== "multiselect" && type !== "memo" && type !== "date"
      ? { type: rawType }
      : {}),
    editable: (params) => {
      return isMilestoneApplicableToRoster(
        params.row.ClassRosterId,
        milestoneName,
      );
    },
    align: "center",
    hideable: false,
    sortable: true,
  };

  if (type === "boolean") {
    return {
      ...baseColumn,
      ...buildBooleanColumn(isMilestoneApplicableToRoster, milestoneName),
    };
  }

  if (type === "multiselect") {
    return {
      ...baseColumn,
      ...buildMultiSelectColumn(
        isMilestoneApplicableToRoster,
        milestoneName,
        getMultiSelectSelectedValues,
      ),
    };
  }

  if (type === "memo") {
    return {
      ...baseColumn,
      ...buildMemoColumn(isMilestoneApplicableToRoster, milestoneName),
    };
  }

  if (type === "date") {
    return {
      ...baseColumn,
      ...buildDateColumn(
        isMilestoneApplicableToRoster,
        milestoneName,
        setError,
      ),
    };
  }

  return {
    ...baseColumn,
    ...buildDefaultColumn(isMilestoneApplicableToRoster, milestoneName),
  };
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const RosterDetails = ({ classRosterId }) => {
  const { roles } = useAuth();

  const [btnDisabled, setBtnDisabled] = useState(true);
  const [openPopover, setOpenPopover] = useState(false);
  const [popoverContent, setPopoverContent] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);

  const [curriculumStatus, setCurriculumStatus] = useState([]);
  const [curriculumValue, setCurriculumValue] = useState(null);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [classRosterDetails, setClassRosterDetails] = useState([]);
  const [opsTrainerShow, setOpsTrainerShow] = useState(true);
  const [searchOpsLead, setSearchOpsLead] = useState([]);
  const [currentEditableRow, setCurrentEditableRow] = useState([]);
  const [refreshGrid, setRefreshGrid] = useState(false);
  const [statusUpdated, setStatusUpdated] = useState(null);
  const [error, setError] = useState(false);
  const [multipleRosterDetails, setMultipleRosterDetails] = useState([]);

  const [activeEditTrainee, setActiveEditTrainee] = useState(null);
  const [hoveredTrainee, setHoveredTrainee] = useState(null);
  const hoverLeaveTimer = useRef(null);

  const bannerTrainee = activeEditTrainee || hoveredTrainee;

  const initialFilterState = {
    items: [
      {
        columnField: "TraineeStatus",
        operatorValue: "is",
        value: "Active",
      },
    ],
  };

  const [filterState, setFilterState] = useState(initialFilterState);

  const handleFilterModelChange = (newModel) => {
    if (newModel.items.length === 0) {
      newModel.items.push({
        columnField: "TraineeStatus",
        operatorValue: "is",
        value: "Active",
      });
    }
    setFilterState({ items: newModel.items });
  };

  const [traineeOptions, setTraineeOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchTraineeOptions = async () => {
      try {
        const traineeStatusOption = await fetchTraineeStatus();

        if (!cancelled && traineeStatusOption) {
          setTraineeOptions(traineeStatusOption);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch trainee status:", error);
        }
      }
    };

    fetchTraineeOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (hoverLeaveTimer.current) {
        clearTimeout(hoverLeaveTimer.current);
      }
    };
  }, []);

  const [sortState, setSortState] = useState([
    {
      field: "TraineeOracleId",
      sort: "asc",
    },
  ]);

  const handleSortModelChange = (newSortModel) => {
    if (newSortModel.length !== sortState.length) {
      setSortState(newSortModel);
      return;
    }
    newSortModel.forEach((element, index) => {
      if (
        element.field !== sortState[index].field ||
        element.sort !== sortState[index].sort
      ) {
        setSortState(newSortModel);
      }
    });
  };

  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleCellEditStart = (params) => {
    setActiveEditTrainee({
      oracleId: params.row.TraineeOracleId,
      traineeName: params.row.TraineeName,
    });
  };

  const handleCellEditStop = (params) => {
    const reason = params?.reason;
    if (
      reason === "escapeKeyDown" ||
      reason === "enterKeyDown" ||
      reason === "tabKeyDown"
    ) {
      setActiveEditTrainee(null);
    }
  };

  const handleRowMouseEnter = (params) => {
    if (hoverLeaveTimer.current) {
      clearTimeout(hoverLeaveTimer.current);
      hoverLeaveTimer.current = null;
    }
    if (!activeEditTrainee) {
      setHoveredTrainee({
        oracleId: params.row.TraineeOracleId,
        traineeName: params.row.TraineeName,
      });
    }
  };

  const handleRowMouseLeave = (e) => {
    const movingIntoHeader = e?.relatedTarget?.closest?.(
      ".MuiDataGrid-columnHeaders",
    );
    if (movingIntoHeader) return;

    hoverLeaveTimer.current = setTimeout(() => {
      setHoveredTrainee(null);
      hoverLeaveTimer.current = null;
    }, 80);
  };

  const handleCurriculumStatusChange = async (event, value) => {
    try {
      setLoading(true);

      const rosterIdsToUpdate =
        multipleRosterDetails && multipleRosterDetails.length > 0
          ? multipleRosterDetails.map((r) => r.ClassRosterId).join(",")
          : classRosterId;

      const response = await updateMergeClassRosterCurriculumStatus(
        rosterIdsToUpdate,
        value.LP_CurriculumStatusId,
      );

      if (response.message === "Success") {
        setCurriculumValue({
          LP_CurriculumStatusId: value.LP_CurriculumStatusId,
          CurriculumStatus: value.CurriculumStatus,
        });

        setMultipleRosterDetails((prev) =>
          prev.map((roster) => ({
            ...roster,
            CurriculumStatusId: value.LP_CurriculumStatusId,
            CurriculumStatus: value.CurriculumStatus,
          })),
        );

        setLoading(false);

        const affectedCount =
          multipleRosterDetails && multipleRosterDetails.length > 0
            ? multipleRosterDetails.length
            : rosterIdsToUpdate.toString().split(",").filter(Boolean).length;

        setSnack({
          open: true,
          message:
            affectedCount > 1
              ? `Curriculum Status has been successfully updated for all ${affectedCount} rosters.`
              : "Curriculum Status has been successfully updated.",
          severity: "success",
        });
      } else {
        setLoading(false);
        setSnack({
          open: true,
          message:
            "Failed to update curriculum status, please contact administrator.",
          severity: "error",
        });
      }
    } catch (error) {
      setLoading(false);
      setSnack({
        open: true,
        message: "Some internal error occurred.",
        severity: "error",
      });
    }
  };

  const handleOnCellClick = async (params, event) => {
    // Clear banner when clicking a different row
    if (
      activeEditTrainee &&
      params.row.TraineeOracleId !== activeEditTrainee.oracleId
    ) {
      setActiveEditTrainee(null);
    }

    if (["TraineeOracleId", "TraineeName"].includes(params.field.toString())) {
      setPopoverContent("");
      if (params.row["HasThirdPartyUpdate"]) {
        const Contentvalue = await getSearchPopoverContent(
          params.row["ClassTraineeId"],
        );
        if (Contentvalue.message === "Success") {
          setPopoverContent(Contentvalue.data[0]["TrackingDetails"]);
          setOpenPopover(true);
          setAnchorEl(event.target);
        }
      }
    }
  };

  const handleOnCellDoubleClick = (params) => {
    if (params.field.toString() === "OperationLeadName") {
      setCurrentEditableRow(params.row);
    }
  };

  const handleClose = () => {
    setOpenPopover(false);
    setAnchorEl(null);
  };

  const getTodayDate = () => {
    return new Date();
  };

  const getOneYearAgoDate = () => {
    const today = getTodayDate();
    return new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
  };

  const oneYearAgo = getOneYearAgoDate();

  const prepareUpdateValue = (value) => {
    if (value === null || value === undefined || value === "") return "null";
    if (typeof value === "string" && value.trim() === "") return "null";
    if (Array?.isArray(value)) {
      return value.length > 0 ? value.join(",") : "multiSelectNoValue";
    }

    return value?.toString();
  };

  const handleCellEditCommit = async ({ id, field, value }) => {
    try {
      if (hasValidationError(error, setSnack)) {
        return;
      }

      const changedRow = rows.find((row) => row.id === id);
      const column = columns.find((col) => col.field === field);

      if (!isCellEditable(column, changedRow, id, field, setSnack)) {
        return;
      }

      setOpsTrainerShow(true);

      if (shouldSkipField(field)) {
        return;
      }

      if (!hasValueChanged(changedRow, field, value)) {
        handleNoChanges(setLoading, setSnack);
        return;
      }

      const updatedValue = getUpdatedValue(
        field,
        value,
        changedRow[field],
        columns,
      );

      await performUpdate(
        classRosterId,
        id,
        field,
        updatedValue,
        prepareUpdateValue,
        setLoading,
        setRows,
        setSnack,
        setActiveEditTrainee,
      );
    } catch (error) {
      setLoading(false);
      setSnack({
        open: true,
        message: "Some internal error occurred",
        severity: "error",
      });
    }
  };

  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const searchHandler = useCallback(
    debounce(async (opsLead) => {
      try {
        setLoading(true);
        if (opsLead && opsLead.length > 2) {
          const valueSearch = await getSearchValue(opsLead);
          if (valueSearch.message === "Success") {
            setSearchOpsLead(
              valueSearch.data.filter((item) => item.Email.length > 0),
            );
            setOpsTrainerShow(false);
            setSnack({
              open: true,
              message: "We've found a matching result for your query",
              severity: "success",
            });
          } else {
            setSnack({
              open: true,
              message: "Couldn't find any results matching your query.",
              severity: "error",
            });
          }
        } else {
          setSnack({
            open: true,
            message: "Please enter at least 3 characters.",
            severity: "error",
          });
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setSnack({
          open: true,
          message: "Some internal error occurred.",
          severity: "error",
        });
      }
    }, 300),
    [],
  );

  const handleOpsLeadChange = async (values) => {
    try {
      if (
        currentEditableRow["OperationalTeamLeadId"] !== values.OracleId &&
        values !== null
      ) {
        setLoading(true);
        const updateClassTraineeGrid =
          await updateMergeClassTraineeMilestoneGridDetails(
            classRosterId,
            currentEditableRow["id"],
            "OperationLeadName",
            prepareUpdateValue(values.OracleId),
          );

        if (updateClassTraineeGrid?.message === "NotFound") {
          document.activeElement.blur();
          setRefreshGrid(true);
        } else {
          setLoading(false);
          setSnack({
            open: true,
            message: "Encounter error while updating changes.",
            severity: "error",
          });
        }
      } else {
        setLoading(false);
        setSnack({
          open: true,
          message: "No change detected.",
          severity: "info",
        });
      }
    } catch (error) {
      setLoading(false);
      setSnack({
        open: true,
        message: "Some internal error occured.",
        severity: "error",
      });
    }
  };

  const handleTraineeStatusChange = async (key, values, oldvalue) => {
    try {
      if (oldvalue !== values.props.value && values !== null) {
        setLoading(true);
        const updateClassTraineeGrid =
          await updateMergeClassTraineeMilestoneGridDetails(
            classRosterId,
            key,
            "TraineeStatus",
            prepareUpdateValue(values.props.value),
          );

        if (updateClassTraineeGrid?.message === "NotFound") {
          document.activeElement.blur();
          setRefreshGrid(true);
        } else {
          setLoading(false);
          setSnack({
            open: true,
            message: "Encounter error while updating changes.",
            severity: "error",
          });
        }
      } else {
        setLoading(false);
        setSnack({
          open: true,
          message: "No change detected.",
          severity: "info",
        });
      }
    } catch (error) {
      setLoading(false);
      setSnack({
        open: true,
        message: "Some internal error occured.",
        severity: "error",
      });
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        if (classRosterId && refreshGrid) {
          if (!cancelled) {
            setLoading(true);
            setBtnDisabled(true);
          }

          const traineeStatusOption = await fetchTraineeStatus();

          if (cancelled) return;

          const staticColumns = prepareStaticColumns(traineeStatusOption);
          await fetchAndPrepareDynamicColumns(staticColumns);

          if (cancelled) return;

          await fetchAndSetupClassRosterGridRows();

          if (!cancelled) {
            setLoading(false);
            setSnack({
              open: true,
              message: "Record has been successfully updated.",
              severity: "success",
            });
            setBtnDisabled(false);
            setRefreshGrid(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setLoading(false);
          setSnack({
            open: true,
            message: "Some internal error occurred.",
            severity: "error",
          });
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [refreshGrid, classRosterId]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        if (classRosterId) {
          if (!cancelled) {
            setLoading(true);
            setBtnDisabled(true);
          }

          const traineeStatusOption = await fetchTraineeStatus();

          if (cancelled) return;

          const staticColumns = prepareStaticColumns(traineeStatusOption);
          await fetchAndPrepareDynamicColumns(staticColumns);

          if (cancelled) return;

          await fetchAndSetupClassRosterGridRows();

          if (!cancelled) {
            setBtnDisabled(false);
            setLoading(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setLoading(false);
          setSnack({
            open: true,
            message: "Some internal error occurred.",
            severity: "error",
          });
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [opsTrainerShow, classRosterId]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        if (classRosterId) {
          if (!cancelled) {
            setLoading(true);
            setBtnDisabled(true);
          }

          const traineeStatusOption = await fetchTraineeStatus();

          if (cancelled) return;

          await fetchcurriculumStatus();

          if (cancelled) return;

          await fetchClassRosterDetails(classRosterId);

          if (cancelled) return;

          const staticColumns = prepareStaticColumns(traineeStatusOption);
          await fetchAndPrepareDynamicColumns(staticColumns);

          if (cancelled) return;

          await fetchAndSetupClassRosterGridRows();

          if (!cancelled) {
            setBtnDisabled(false);
            setLoading(false);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setLoading(false);
          setSnack({
            open: true,
            message: "Some internal error occurred.",
            severity: "error",
          });
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [classRosterId]);

  const fetchTraineeStatus = async () => {
    const ALLOWED_ACCESS_LEVELS = [
      "LD_Leader",
      "LD_Trainer",
      "LD_Group",
      "LD_Group_Admin",
      "Ops_Group",
    ];

    const checkUserAccessLevel = (accessLevel) => {
      return (
        ALLOWED_ACCESS_LEVELS.includes(accessLevel) &&
        roles.includes(accessLevel)
      );
    };

    try {
      const traineeStatus = await getTraineeStatusList();
      if (traineeStatus?.message === "Success") {
        const updatedStatus = traineeStatus.data.map((item) => {
          const label = item.label.toLowerCase();
          const isAdmin = checkUserAccessLevel("LD_Group_Admin");
          const isTrainer =
            checkUserAccessLevel("LD_Trainer") ||
            checkUserAccessLevel("LD_Group");
          const isLeader = checkUserAccessLevel("LD_Leader");
          const isOpsGroup = checkUserAccessLevel("Ops_Group");

          const disabled =
            (label === "active" && isAdmin) ||
            (label === "added in error" && isAdmin) ||
            (label === "pending r/t" &&
              (isAdmin || isOpsGroup || isTrainer || isLeader));

          return {
            ...item,
            disabled,
          };
        });

        return updatedStatus;
      }
    } catch (error) {
      setSnack({
        open: true,
        message: "Error fetching trainee status",
        severity: "error",
      });
    }
  };

  const fetchcurriculumStatus = async () => {
    const curriculumStatus = await getCurriculumStatus();
    if (curriculumStatus?.message === "Success") {
      setCurriculumStatus(curriculumStatus?.data);
    }
  };

  const validateAndGetOpsLeadName = (item, params, value) => {
    if (item.ClassTraineeId === params.row.ClassTraineeId)
      return {
        ...item,
        OperationLeadName: value,
      };
    else {
      return item;
    }
  };

  const formatMultipleValues = (fieldName) => {
    if (multipleRosterDetails.length === 0) {
      switch (fieldName) {
        case "classRosterId":
          return classRosterDetails.ClassRosterId || "";
        case "className":
          return classRosterDetails.ClassName || "";
        case "project":
          return classRosterDetails.Project || "";
        default:
          return "";
      }
    }

    const values = multipleRosterDetails
      .map((roster) => {
        switch (fieldName) {
          case "classRosterId":
            return roster.ClassRosterId;
          case "className":
            return roster.ClassName;
          case "project":
            return roster.Project;
          default:
            return "";
        }
      })
      .filter((val) => val);

    const uniqueValues = [...new Set(values)];
    return uniqueValues.join(", ");
  };

  const fetchClassRosterDetails = async (classRosterId) => {
    const classRosterDetails = await getMergeClassRosterDetails(classRosterId);

    if (classRosterDetails?.message === "Success") {
      const rosterData = classRosterDetails?.data || [];

      setMultipleRosterDetails(rosterData);
      setClassRosterDetails(rosterData[0] || {});
    }
  };

  useEffect(() => {
    if (!multipleRosterDetails || multipleRosterDetails.length === 0) {
      setCurriculumValue(null);
      return;
    }

    const first = multipleRosterDetails[0].CurriculumStatusId;
    const allSame = multipleRosterDetails.every(
      (r) => r.CurriculumStatusId === first,
    );

    if (!allSame) {
      setCurriculumValue(null);
      return;
    }

    if (first === null || first === undefined) {
      setCurriculumValue(null);
      return;
    }

    const id = Number(first);
    const option = curriculumStatus.find(
      (o) => Number(o.LP_CurriculumStatusId) === id,
    );

    if (option) {
      setCurriculumValue(option);
    } else {
      const rosterMatch = multipleRosterDetails.find(
        (r) => r.CurriculumStatusId === first,
      );
      setCurriculumValue({
        LP_CurriculumStatusId: id,
        CurriculumStatus: rosterMatch?.CurriculumStatus || "",
      });
    }
  }, [multipleRosterDetails, curriculumStatus]);

  // ============================================================================
  // MULTISELECT FIX: Enhanced function to handle all possible input formats
  // Handles null/undefined, arrays (during editing), and strings (from database)
  // Ensures consistent array output for multiselect fields regardless of source
  // ============================================================================

  const getMultiSelectSelectedValues = (value) => {
    if (!value || value === "null" || value === "undefined") return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      return value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }
    return [];
  };

  const prepareStaticColumns = (traineeStatusOption) => [
    {
      field: "ClassRosterId",
      headerName: "Roster ID",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "string",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
    },
    {
      field: "ClassTraineeId",
      headerName: "Trainee Id",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "number",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
      flex: 1,
    },
    {
      field: "TraineeOracleId",
      headerName: "Oracle Id",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "number",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {params.row.HasThirdPartyUpdate && (
            <CustomTooltip
              title={
                <span>
                  Click on <strong>Oracle Id</strong> OR{" "}
                  <strong>Trainee Name</strong> value for more info.
                </span>
              }
              placement="bottom-end"
            >
              <InfoIcon sx={{ color: "#3299FF", marginRight: 1 }} />
            </CustomTooltip>
          )}
          {params.value}
        </Box>
      ),
    },
    {
      field: "TraineeName",
      headerName: "Trainee Name",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "string",
      valueGetter: (params) => {
        const [firstName, lastName] = params.value.split(" ");
        return `${lastName} ${firstName}`;
      },
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
      width: 200,
    },
    {
      field: "TraineeComment",
      headerName: "Comment",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "string",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
    },
    {
      field: "EmployeeStatus",
      headerName: "Employee Status",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "string",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
      width: 150,
    },
    {
      field: "TraineeStatus",
      headerName: "Trainee Status",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "singleSelect",
      valueOptions: traineeOptions.map((option) => `${option.label}`),
      filterOperators: getGridSingleSelectOperators().filter(
        (operator) => operator.value === "is",
      ),
      renderEditCell: (params) => {
        const isUpdated = statusUpdated === params.id;
        return isUpdated ? (
          <span>
            {traineeStatusOption.find(
              (option) => option.value === params.row.TraineeStatusId,
            )?.label || ""}
          </span>
        ) : (
          <Select
            sx={{
              backgroundColor: "#f0f0f0",
              borderRadius: "4px",
            }}
            value={params.row.TraineeStatusId}
            onChange={(_event, values) => {
              handleTraineeStatusChange(
                params.row.ClassTraineeId,
                values,
                params.row.TraineeStatusId,
              );
              setStatusUpdated(params.id);
            }}
            autoFocus
          >
            {traineeStatusOption.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={!option.disabled}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        );
      },
      valueGetter: (params) => {
        const status = traineeStatusOption.find(
          (option) => option.value === params.row.TraineeStatusId,
        );
        return status ? status.label : "";
      },
      hideable: false,
      sortable: true,
      editable: true,
      align: "center",
      width: 150,
    },
    {
      field: "FTRR",
      headerName: "FTRR",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "boolean",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
      flex: 1,
    },
    {
      field: "EndorsedToNestingStatus",
      headerName: "E2N",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "boolean",
      hideable: false,
      sortable: true,
      editable: true,
      align: "center",
      flex: 1,
    },
    {
      field: "E2P",
      headerName: "E2P",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "boolean",
      hideable: false,
      sortable: true,
      editable: true,
      align: "center",
      flex: 1,
    },
    {
      field: "OperationalTeamLeadId",
      headerName: "Ops Lead Id",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "number",
      hideable: false,
      sortable: true,
      editable: false,
      align: "right",
    },
    {
      field: "OperationLeadName",
      headerName: "Operation Lead",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "string",
      renderEditCell: (params) => (
        <OperationLeadEditCell
          params={params}
          opsTrainerShow={opsTrainerShow}
          setRows={setRows}
          validateAndGetOpsLeadName={validateAndGetOpsLeadName}
          searchHandler={searchHandler}
          searchOpsLead={searchOpsLead}
          handleOpsLeadChange={handleOpsLeadChange}
          PaperComponentTemplate={PaperComponentTemplate}
          classes={classes}
        />
      ),
      hideable: false,
      sortable: true,
      editable: true,
      align: "center",
      width: 250,
    },
    {
      field: "ProductionDate",
      headerName: "Production Date",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "date",
      renderCell: (params) => {
        format(new Date(params.row.ProductionDate), "MM/dd/yyyy");
      },
      valueFormatter: (params) => {
        return format(new Date(params.value), "MM/dd/yyyy");
      },
      valueGetter: (params) => {
        return format(new Date(params.value), "MM/dd/yyyy");
      },
      preProcessEditCellProps: (params) => {
        const dateValue = new Date(params.props.value);
        const hasError = dateValue < oneYearAgo;
        if (hasError) {
          setSnack({
            open: true,
            message: "Production Date cannot be older than one year.",
            severity: "error",
          });
        }
        return { ...params.props, error: hasError };
      },
      hideable: false,
      sortable: true,
      editable: true,
      align: "center",
    },
    {
      field: "Project",
      headerName: "Project",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "string",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
      width: 200,
    },
    {
      field: "Site",
      headerName: "Site",
      headerClassName: classes.headerClass,
      headerAlign: "center",
      type: "string",
      hideable: false,
      sortable: true,
      editable: false,
      align: "center",
      width: 200,
    },
  ];

  const fetchAndPrepareDynamicColumns = async (staticColumns) => {
    const dynamicMilestoneList =
      await getMilestoneByMergeRosterId(classRosterId);

    if (dynamicMilestoneList?.message !== "Success") {
      setColumns([...staticColumns]);
      return;
    }

    const rosterToMilestones = {};
    const milestoneDefinitions = {};

    dynamicMilestoneList.data.forEach((rosterData) => {
      const rosterId = String(rosterData.ClassRosterId);

      if (!rosterToMilestones[rosterId]) {
        rosterToMilestones[rosterId] = new Set();
      }

      Object.entries(rosterData).forEach(([key, value]) => {
        if (key !== "ClassRosterId" && value !== null && value !== undefined) {
          rosterToMilestones[rosterId].add(key);

          if (!milestoneDefinitions[key]) {
            milestoneDefinitions[key] = value;
          }
        }
      });
    });

    const allMilestones = new Set();
    Object.values(rosterToMilestones).forEach((milestoneSet) => {
      milestoneSet.forEach((milestone) => allMilestones.add(milestone));
    });

    const isMilestoneApplicableToRoster = (traineeRosterId, milestoneName) => {
      const rosterIdStr = String(traineeRosterId);
      return rosterToMilestones[rosterIdStr]?.has(milestoneName) || false;
    };

    const dynamicColumns = Array.from(allMilestones).map((milestoneName) =>
      buildDynamicColumn(
        milestoneName,
        milestoneDefinitions,
        isMilestoneApplicableToRoster,
        prepareDynamicColumnsValueOptions,
        getMultiSelectSelectedValues,
        setError,
        classes,
      ),
    );

    setColumns([...staticColumns, ...dynamicColumns]);
  };

  const fetchAndSetupClassRosterGridRows = async () => {
    const classTraineeMilestoneGrid =
      await getMergeClassTraineeMilestoneDetails(classRosterId);

    if (classTraineeMilestoneGrid?.message === "Success") {
      const rowsData = Object.values(classTraineeMilestoneGrid.data)?.map(
        (item) => {
          return { ...item, id: item.ClassTraineeId };
        },
      );

      const uniqueRosterIds = [
        ...new Set(rowsData.map((r) => r.ClassRosterId)),
      ];
      const filteredRows = rowsData.filter(
        (column) => column["TraineeStatus"]?.toLowerCase() !== "added in error",
      );

      const selectedRosterIds = classRosterId
        .toString()
        .split(",")
        .map((id) => parseInt(id.trim()));
      const rostersWithNoData = selectedRosterIds.filter(
        (rosterId) => !uniqueRosterIds.includes(rosterId),
      );

      if (rostersWithNoData.length > 0) {
        setSnack({
          open: true,
          message: `No trainee data found for Roster ID(s): ${rostersWithNoData.join(
            ", ",
          )}`,
          severity: "error",
        });
      }
      if (filteredRows.length === 0) {
        setSnack({
          open: true,
          message: "No trainee records found for the selected roster(s)",
          severity: "warning",
        });
      }

      setRows(filteredRows);
    } else {
      setSnack({
        open: true,
        message: `Records not found for Roster ID: ${classRosterId}`,
        severity: "error",
      });
    }
  };

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Grid
        container
        direction="row"
        justifyContent="space-evenly"
        alignItems="center"
        spacing={1}
        sx={{ padding: 2 }}
      >
        <Grid item md={1}>
          <CustomButton
            variant="contained"
            sx={{ borderRadius: 0 }}
            startIcon={<DownloadIcon />}
            onClick={() => exportAsCsv(rows, columns, "LPClassRoster")}
            disabled={btnDisabled}
          >
            EXPORT
          </CustomButton>
        </Grid>
        <Grid item md={1.5}>
          <b>Class Roster:</b>{" "}
          {formatMultipleValues("classRosterId") ||
            classRosterDetails.ClassRosterId ||
            "N/A"}
        </Grid>
        <Grid item md="auto">
          <b>Class Name:</b>{" "}
          {formatMultipleValues("className") ||
            classRosterDetails.ClassName ||
            "N/A"}
        </Grid>
        <Grid item md="auto">
          <b>Project:</b>{" "}
          {formatMultipleValues("project") ||
            classRosterDetails.Project ||
            "N/A"}
        </Grid>
        <Grid item md="auto">
          <b>Curriculum Status:</b>
        </Grid>
        <Grid item md="auto">
          <Autocomplete
            disablePortal
            freeSolo
            openOnFocus
            sx={{
              width: 200,
              opacity: 1,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: "gray",
              borderRadius: 32,
            }}
            value={curriculumValue || null}
            disableClearable={false}
            disabled={btnDisabled}
            options={curriculumStatus}
            getOptionLabel={(option) => option?.CurriculumStatus || ""}
            isOptionEqualToValue={(option, value) =>
              option?.LP_CurriculumStatusId === value?.LP_CurriculumStatusId
            }
            onChange={(event, value) => {
              if (value) {
                handleCurriculumStatusChange(event, value);
              }
            }}
            className={classes.searchField}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 32,
                    border: "1px solid #d3d3d3",
                    "& fieldset": {
                      border: "none",
                    },
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid item md={12} sx={{ display: "flex", flexDirection: "column" }}>
          {/* INFO BANNER */}
          {bannerTrainee && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 0.75,
                mb: 1,
                backgroundColor: activeEditTrainee ? "#e8f4ff" : "#f5f5f5",
                border: `1px solid ${activeEditTrainee ? "#3299FF" : "#bdbdbd"}`,
                borderRadius: "8px",
                fontFamily: "Urbanist, sans-serif",
                fontSize: "1rem",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.10)",
                transition:
                  "background-color 0.2s ease, border-color 0.2s ease",
              }}
            >
              <InfoIcon
                sx={{
                  fontSize: "1.1rem",
                  color: activeEditTrainee ? "#3299FF" : "#9e9e9e",
                }}
              />
              <span>
                {activeEditTrainee ? "Editing Trainee: " : "Trainee: "}
                <strong>{bannerTrainee.traineeName}</strong>
                <span style={{ color: "#666", marginLeft: 6 }}>
                  Oracle ID:<strong>{bannerTrainee.oracleId}</strong>
                </span>
              </span>
            </Box>
          )}

          <Box className={classes.dataGridContainer} sx={{ flexGrow: 1 }}>
            <DataGrid
              sx={{
                height: "100%",
                "& .MuiDataGrid-columnHeaders": {
                  position: "sticky",
                  zIndex: 1000,
                },
                "& .MuiDataGrid-virtualScroller": {
                  marginTop: "0 !important",
                },
                "& .MuiDataGrid-main": {
                  overflow: "clip",
                  height: "100%",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#3299FF",
                },
                "& .MuiDataGrid-filterIcon": {
                  color: "#3299FF",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontFamily: "Urbanist, sans-serif",
                  fontWeight: "bold",
                },

                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f0f7ff",
                  cursor: "pointer",
                },
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: "#daeeff",
                },
              }}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    ClassTraineeId: false,
                    OperationalTeamLeadId: false,
                  },
                },
              }}
              columns={columns}
              rows={rows}
              getRowId={(row) => row.id}
              pageSize={100}
              disableSelectionOnClick
              rowsPerPageOptions={[100]}
              components={{ NoRowsOverlay: CustomNoRowsOverlay }}
              sortModel={sortState}
              onSortModelChange={handleSortModelChange}
              filterModel={filterState}
              onFilterModelChange={handleFilterModelChange}
              onCellClick={handleOnCellClick}
              onCellDoubleClick={handleOnCellDoubleClick}
              onCellEditCommit={handleCellEditCommit}
              editMode="cell"
              onCellEditStart={handleCellEditStart}
              onCellEditStop={handleCellEditStop}
              componentsProps={{
                row: {
                  onMouseEnter: (e) => {
                    const rowId = Number(
                      e.currentTarget.getAttribute("data-id"),
                    );
                    const row = rows.find((r) => r.id === rowId);
                    if (row) handleRowMouseEnter({ row });
                  },
                  onMouseLeave: (e) => handleRowMouseLeave(e),
                },
              }}
            />
            {openPopover && anchorEl && (
              <Popover
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                open={openPopover}
                onClose={handleClose}
                disableRestoreFocus
              >
                <Box sx={{ p: 1.25, bgcolor: "background.paper" }}>
                  <p
                    className={classes.rowheight}
                    dangerouslySetInnerHTML={{ __html: popoverContent }}
                  ></p>
                </Box>
              </Popover>
            )}
          </Box>
        </Grid>
      </Grid>
      <SnackBar snack={snack} snackReset={snackReset} />
    </>
  );
};

RosterDetails.propTypes = {
  classRosterId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default RosterDetails;
